// Readiness-Aware Provider Wrappers
// These wrappers integrate individual context providers with the ContextReadinessManager
import React, { ReactNode, useEffect } from 'react';

// Import the readiness hook
import { useRegisterContextReadiness } from './ContextReadinessManager';

// Import original providers
import { AuthProvider as OriginalAuthProvider, useAuth } from './AuthContext';
import { VRPDataProvider as OriginalVRPDataProvider, useVRPData } from './VRPDataContext';
import { VRPUIProvider as OriginalVRPUIProvider, useVRPUI } from './VRPUIContext';
import { VRPNotificationProvider as OriginalVRPNotificationProvider } from './VRPNotificationContext';
import { VRPErrorBoundaryProvider as OriginalVRPErrorBoundaryProvider } from './VRPErrorBoundaryProvider';
import { DomainThemeProvider as OriginalDomainThemeProvider, useDomainTheme } from './DomainThemeContext';
import { ProjectProvider as OriginalProjectProvider } from './ProjectContext';
import { ScenarioProvider as OriginalScenarioProvider } from './ScenarioContext';

// Auth Provider Wrapper
export const ReadinessAwareAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <OriginalAuthProvider>
      <AuthReadinessTracker>
        {children}
      </AuthReadinessTracker>
    </OriginalAuthProvider>
  );
};

const AuthReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { loading } = useAuth();
  
  // Register auth readiness when loading is complete
  useRegisterContextReadiness('auth', !loading);
  
  return <>{children}</>;
};

// Data Provider Wrapper  
export const ReadinessAwareDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <OriginalVRPDataProvider>
      <DataReadinessTracker>
        {children}
      </DataReadinessTracker>
    </OriginalVRPDataProvider>
  );
};

const DataReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { loading } = useVRPData();
  
  // Register data readiness when loading is complete
  useRegisterContextReadiness('data', !loading);
  
  return <>{children}</>;
};

// UI Provider Wrapper
export const ReadinessAwareUIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <OriginalVRPUIProvider>
      <UIReadinessTracker>
        {children}
      </UIReadinessTracker>
    </OriginalVRPUIProvider>
  );
};

const UIReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { state } = useVRPUI();
  
  // UI is ready when state is initialized (has the basic structure)
  const isReady = Boolean(state && typeof state === 'object' && state.theme);
  useRegisterContextReadiness('ui', isReady);
  
  return <>{children}</>;
};

// Theme Provider Wrapper
export const ReadinessAwareThemeProvider: React.FC<{ 
  children: ReactNode;
  initialDomain?: string;
}> = ({ children, initialDomain }) => {
  return (
    <OriginalDomainThemeProvider initialDomain={initialDomain as any}>
      <ThemeReadinessTracker>
        {children}
      </ThemeReadinessTracker>
    </OriginalDomainThemeProvider>
  );
};

const ThemeReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentDomain } = useDomainTheme();
  
  // Theme is ready when domain is loaded
  const isReady = Boolean(currentDomain);
  useRegisterContextReadiness('theme', isReady);
  
  return <>{children}</>;
};

// Notification Provider Wrapper
export const ReadinessAwareNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Notifications are ready immediately when provider mounts
  useRegisterContextReadiness('notifications', true);
  
  return (
    <OriginalVRPNotificationProvider>
      {children}
    </OriginalVRPNotificationProvider>
  );
};

// Error Boundary Provider Wrapper
export const ReadinessAwareErrorBoundaryProvider: React.FC<{ 
  children: ReactNode;
  level?: string;
  enableRecovery?: boolean;
  fallback?: (error: Error, retry: () => void) => React.ReactNode;
}> = ({ children, level = "application", enableRecovery = false, fallback }) => {
  // Error boundary is ready immediately when provider mounts
  useRegisterContextReadiness('errorBoundary', true);
  
  return (
    <OriginalVRPErrorBoundaryProvider
      level={level}
      enableRecovery={enableRecovery}
      fallback={fallback}
    >
      {children}
    </OriginalVRPErrorBoundaryProvider>
  );
};

// Project Provider Wrapper
export const ReadinessAwareProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <OriginalProjectProvider>
      <ProjectReadinessTracker>
        {children}
      </ProjectReadinessTracker>
    </OriginalProjectProvider>
  );
};

const ProjectReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Project context needs to access useProject hook, but we need to be careful with imports
  // For now, we'll mark it as ready after a brief initialization delay
  const [isReady, setIsReady] = React.useState(false);
  
  useEffect(() => {
    // Small delay to allow project context to initialize
    const timeout = setTimeout(() => {
      setIsReady(true);
      console.log('📂 [ProjectReadiness] Project system initialized');
    }, 150);
    
    return () => clearTimeout(timeout);
  }, []);
  
  useRegisterContextReadiness('project', isReady);
  
  return <>{children}</>;
};

// Scenario Provider Wrapper
export const ReadinessAwareScenarioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <OriginalScenarioProvider>
      <ScenarioReadinessTracker>
        {children}
      </ScenarioReadinessTracker>
    </OriginalScenarioProvider>
  );
};

const ScenarioReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Scenario context initialization
  const [isReady, setIsReady] = React.useState(false);
  
  useEffect(() => {
    // Small delay to allow scenario context to initialize
    const timeout = setTimeout(() => {
      setIsReady(true);
      console.log('📋 [ScenarioReadiness] Scenario system initialized');
    }, 200);
    
    return () => clearTimeout(timeout);
  }, []);
  
  useRegisterContextReadiness('scenario', isReady);
  
  return <>{children}</>;
};

// Navigation readiness tracker (for when navigation context is provided elsewhere)
export const NavigationReadinessTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Navigation is considered ready when this component mounts
  // This would be used in routing components or navigation providers
  useEffect(() => {
    // Mark navigation as ready after a brief delay to ensure routing is initialized
    const timeout = setTimeout(() => {
      // This would be called from a hook that registers navigation readiness
      console.log('📍 [NavigationReadiness] Navigation system initialized');
    }, 100);
    
    return () => clearTimeout(timeout);
  }, []);
  
  // For now, immediately mark navigation as ready
  useRegisterContextReadiness('navigation', true);
  
  return <>{children}</>;
};

// Hook to manually mark navigation readiness (for use in routing components)
export const useMarkNavigationReady = () => {
  useRegisterContextReadiness('navigation', true);
};

// Readiness debug component
export const ReadinessDebugInfo: React.FC = () => {
  const [showDebug, setShowDebug] = React.useState(false);
  
  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }
  
  return (
    <div style={{ position: 'fixed', top: 10, left: 10, zIndex: 10000 }}>
      <button
        onClick={() => setShowDebug(!showDebug)}
        style={{
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          border: 'none',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          cursor: 'pointer'
        }}
      >
        {showDebug ? 'Hide' : 'Show'} Context Status
      </button>
      
      {showDebug && (
        <div style={{
          marginTop: '4px',
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '12px',
          minWidth: '200px'
        }}>
          <div>Context Readiness Debug</div>
          <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px' }}>
            Use ContextReadinessManager hooks to track detailed status
          </div>
        </div>
      )}
    </div>
  );
};

// Export pattern for easier provider replacement
export const ReadinessAwareProviders = {
  Auth: ReadinessAwareAuthProvider,
  Data: ReadinessAwareDataProvider,
  UI: ReadinessAwareUIProvider,
  Theme: ReadinessAwareThemeProvider,
  Project: ReadinessAwareProjectProvider,
  Scenario: ReadinessAwareScenarioProvider,
  Notifications: ReadinessAwareNotificationProvider,
  ErrorBoundary: ReadinessAwareErrorBoundaryProvider,
  NavigationTracker: NavigationReadinessTracker
};

export default ReadinessAwareProviders;